import { Typography, ButtonPro, InputPro } from 'components';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import { EditProjectModal } from './EditProjectModal';
import { EditDesignationModal } from './EditDesignationModal';
import { AddProjectModal } from './AddProjectModal';
import { AddDesignationModal } from './AddDesignationModal';
import './index.scss';
import {
  getDesignations,
  getEmployees,
  getProjects,
  updateDesignation,
  updateProject
} from 'store/MasterData';

export default function ManagementDashboard() {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<'projects' | 'designations'>(
    'projects'
  );
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
  const [isEditDesignationModalOpen, setIsEditDesignationModalOpen] =
    useState(false);
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
  const [isAddDesignationModalOpen, setIsAddDesignationModalOpen] =
    useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedDesignation, setSelectedDesignation] = useState<any>(null);

  const { projects, designations } = useAppSelector(
    (state) => state.masterData
  );

  useEffect(() => {
    dispatch(getProjects());
    dispatch(getDesignations());
    dispatch(getEmployees());
  }, [dispatch]);

  // Filter projects based on search term
  const filteredProjects = projects?.filter(
    (project) =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.projectManager.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.technicalLead.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter designations based on search term
  const filteredDesignations = designations?.filter(
    (designation) =>
      designation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      designation.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handler functions
  const handleEditProject = (project: any) => {
    setSelectedProject(project);
    setIsEditProjectModalOpen(true);
  };

  const handleEditDesignation = (designation: any) => {
    setSelectedDesignation(designation);
    setIsEditDesignationModalOpen(true);
  };

  const handleSaveProject = (updatedProject: any) => {
    dispatch(updateProject(updatedProject));
  };

  const handleSaveDesignation = (updatedDesignation: any) => {
    dispatch(updateDesignation(updatedDesignation));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Ongoing';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  return (
    <div className="employee-summary-dashboard">
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-info">
            <Typography
              text="Management Dashboard"
              type="heading2"
              weight="bold"
              className="dashboard-title"
            />
            <Typography
              text="Manage projects and designations"
              type="small_regular"
              className="dashboard-subtitle"
            />
          </div>
          <div className="header-actions">
            <ButtonPro
              label={`Add New ${
                activeTab === 'projects' ? 'Project' : 'Designation'
              }`}
              type="primary"
              size="base"
              onClick={() => {
                if (activeTab === 'projects') {
                  setIsAddProjectModalOpen(true);
                } else {
                  setIsAddDesignationModalOpen(true);
                }
              }}
            />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <ButtonPro
            label={`Projects (${projects?.length})`}
            type={activeTab === 'projects' ? 'primary' : 'text'}
            size="base"
            onClick={() => setActiveTab('projects')}
            buttonClassNames={`tab-button ${
              activeTab === 'projects' ? 'active' : ''
            }`}
          />
          <ButtonPro
            label={`Designations (${designations?.length})`}
            type={activeTab === 'designations' ? 'primary' : 'text'}
            size="base"
            onClick={() => setActiveTab('designations')}
            buttonClassNames={`tab-button ${
              activeTab === 'designations' ? 'active' : ''
            }`}
          />
        </div>

        {/* Search Filter */}
        <div className="dashboard-filters">
          <div className="filter-group">
            <div className="filter-item">
              <InputPro
                name="search"
                id="search"
                label={`Search ${activeTab}`}
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(value) => setSearchTerm(value)}
              />
            </div>
          </div>
        </div>

        {/* Projects Table */}
        {activeTab === 'projects' && (
          <div className="table-container">
            <div className="table-header">
              <Typography
                text="Projects"
                type="base_medium"
                weight="bold"
                className="table-title"
              />
              <Typography
                text={`${filteredProjects.length} projects found`}
                type="small_regular"
                className="table-count"
              />
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Project Name</th>
                    <th>Description</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Project Manager</th>
                    <th>Technical Lead</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.length > 0 ? (
                    filteredProjects.map((project) => (
                      <tr key={project.id}>
                        <td className="project-name">
                          <div className="name-cell">
                            <span className="name">{project.name}</span>
                          </div>
                        </td>
                        <td className="description">
                          <span title={project.description}>
                            {project.description.length > 50
                              ? `${project.description.substring(0, 50)}...`
                              : project.description}
                          </span>
                        </td>
                        <td className="date-cell">
                          {formatDate(project.startDate)}
                        </td>
                        <td className="date-cell">
                          {formatDate(project.endDate)}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              project.isActive ? 'active' : 'inactive'
                            }`}
                          >
                            {project.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="manager-cell">
                          {project.projectManager || 'Not Assigned'}
                        </td>
                        <td className="lead-cell">
                          {project.technicalLead || 'Not Assigned'}
                        </td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <ButtonPro
                              label="Edit"
                              type="text"
                              size="small"
                              onClick={() => handleEditProject(project)}
                              buttonClassNames="action-btn edit-btn"
                            />
                            <ButtonPro
                              label="Delete"
                              type="red"
                              size="small"
                              onClick={() =>
                                console.log('Delete project:', project.id)
                              }
                              buttonClassNames="action-btn delete-btn"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="no-data">
                        No projects found matching your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Designations Table */}
        {activeTab === 'designations' && (
          <div className="table-container">
            <div className="table-header">
              <Typography
                text="Designations"
                type="base_medium"
                weight="bold"
                className="table-title"
              />
              <Typography
                text={`${filteredDesignations.length} designations found`}
                type="small_regular"
                className="table-count"
              />
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Designation Name</th>
                    <th>Description</th>
                    <th>Created Date</th>
                    <th>Modified Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDesignations.length > 0 ? (
                    filteredDesignations.map((designation) => (
                      <tr key={designation.id}>
                        <td className="designation-name">
                          <div className="name-cell">
                            <span className="name">{designation.name}</span>
                          </div>
                        </td>
                        <td className="description">
                          <span title={designation.description}>
                            {designation.description.length > 80
                              ? `${designation.description.substring(0, 80)}...`
                              : designation.description}
                          </span>
                        </td>
                        <td className="date-cell">
                          {formatDate(designation.createdDate)}
                        </td>
                        <td className="date-cell">
                          {formatDate(designation.modifiedDate)}
                        </td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <ButtonPro
                              label="Edit"
                              type="text"
                              size="small"
                              onClick={() => handleEditDesignation(designation)}
                              buttonClassNames="action-btn edit-btn"
                            />
                            <ButtonPro
                              label="Delete"
                              type="red"
                              size="small"
                              onClick={() =>
                                console.log(
                                  'Delete designation:',
                                  designation.id
                                )
                              }
                              buttonClassNames="action-btn delete-btn"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="no-data">
                        No designations found matching your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modals */}
      <EditProjectModal
        isOpen={isEditProjectModalOpen}
        project={selectedProject}
        onClose={() => setIsEditProjectModalOpen(false)}
        onSave={handleSaveProject}
      />

      <EditDesignationModal
        isOpen={isEditDesignationModalOpen}
        designation={selectedDesignation}
        onClose={() => setIsEditDesignationModalOpen(false)}
        onSave={handleSaveDesignation}
      />

      {/* Add New Modals */}
      <AddProjectModal
        isOpen={isAddProjectModalOpen}
        onClose={() => setIsAddProjectModalOpen(false)}
      />

      <AddDesignationModal
        isOpen={isAddDesignationModalOpen}
        onClose={() => setIsAddDesignationModalOpen(false)}
      />
    </div>
  );
}
